// What the Lead finder pages share: the section frame, the badges and the
// Start a run dialog.

import { useState, type ChangeEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { CandidateStatus, FinderAdapter, FinderStatus, RunStatus, SearchKind, StartRunBody } from '@/api/growthInsights'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, Button, Dialog, FormField, InlineAlert, PlusIcon, RadioCards, SelectInput, Tabs, Textarea } from '@/components/ui'
import { formatCount } from '@/lib/format'
import { CANDIDATE_STATUS_LABELS, CANDIDATE_STATUS_TONE, OUTCOME_LABELS, OUTCOME_TONE, RUN_STATUS_LABELS, RUN_STATUS_TONE, SEARCH_KIND_LABELS } from '@/lib/growthInsights'
import { invalidateQueries } from '@/lib/query'
import { useFinderStatus, usePoll } from '@/lib/useFinder'

export function CandidateStatusBadge({ status }: { status: CandidateStatus }) {
  return <Badge tone={CANDIDATE_STATUS_TONE[status]}>{CANDIDATE_STATUS_LABELS[status]}</Badge>
}

export function RunStatusBadge({ status }: { status: RunStatus }) {
  return <Badge tone={RUN_STATUS_TONE[status]}>{RUN_STATUS_LABELS[status]}</Badge>
}

export function OutcomeBadge({ outcome }: { outcome: string }) {
  return <Badge tone={OUTCOME_TONE[outcome] ?? 'outline'}>{OUTCOME_LABELS[outcome] ?? outcome}</Badge>
}

function RunDialog({ open, onClose, status }: { open: boolean; onClose: () => void; status: FinderStatus }) {
  const navigate = useNavigate()
  const [adapter, setAdapter] = useState<FinderAdapter>('manual')
  const [urls, setUrls] = useState('')
  const [kinds, setKinds] = useState<SearchKind[]>(['youth', 'church', 'rec'])
  const [mode, setMode] = useState<'builtin' | 'platform'>('builtin')
  const [platformId, setPlatformId] = useState('')
  const [state, setState] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const info = status.adapters.find((a) => a.key === adapter)
  const search = adapter === 'web_search'

  const close = () => {
    if (pending) return
    setErrors({})
    setProblem(null)
    onClose()
  }

  const submit = async () => {
    setErrors({})
    setProblem(null)
    const body: StartRunBody = !search
      ? { adapter: adapter as 'manual' | 'recleague' | 'quickscores', urls }
      : mode === 'platform'
        ? { adapter: 'web_search', mode: 'platform', platform_id: Number(platformId), state }
        : { adapter: 'web_search', kinds, state }
    setPending(true)
    try {
      const run = await api.growth.finder.startRun(body)
      invalidateQueries('growth')
      setPending(false)
      setUrls('')
      onClose()
      navigate(`/growth/finder/runs/${run.id}`)
    } catch (err) {
      setPending(false)
      if (isApiError(err) && err.field && err.field !== 'adapter') setErrors({ [err.field]: err.message })
      else setProblem(errorMessage(err))
    }
  }

  const toggleKind = (kind: SearchKind) => (e: ChangeEvent<HTMLInputElement>) =>
    setKinds((prev) => (e.target.checked ? [...prev, kind] : prev.filter((k) => k !== kind)))

  const placeholder = adapter === 'manual' ? 'https://example.org/basketball\nhttps://www.facebook.com/somepage Some League' : adapter === 'recleague' ? 'osfallbasketballleague\nhttps://www.recleague.net/leagues/…' : 'oceansprings\nhttps://www.quickscores.com/…'

  return (
    <Dialog
      open={open}
      onClose={close}
      dismissible={!pending}
      size="lg"
      title="Start a run"
      description="Nothing becomes a lead until you add it. Pages are read slowly: at most one every 3 seconds per site."
      footer={
        <>
          <Button onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" loading={pending} disabled={info ? !info.available : false} onClick={() => void submit()}>
            Start run
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
        <RadioCards
          legend="Where to look"
          value={adapter}
          columns={2}
          onChange={setAdapter}
          options={status.adapters.map((a) => ({
            value: a.key,
            label: a.label,
            description: a.available ? a.description : `Off. Set ${a.missing.join(' and ')} on the backend.`,
          }))}
        />
        {search ? (
          info?.available ? (
            <>
              <RadioCards
                legend="What to search"
                value={mode}
                columns={2}
                onChange={setMode}
                options={[
                  { value: 'builtin', label: 'By league type', description: 'The built-in youth, church and rec searches.' },
                  { value: 'platform', label: 'By platform', description: "One platform's public league pages." },
                ]}
              />
              {mode === 'platform' ? (
                <FormField label="Platform" required hint={platformId ? `Searches site:${status.search.platforms.find((p) => String(p.id) === platformId)?.site}` : undefined} error={errors.platform_id ?? null}>
                  {({ id, describedBy, invalid }) => (
                    <SelectInput
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      value={platformId}
                      options={[{ value: '', label: 'Pick a platform' }, ...status.search.platforms.map((p) => ({ value: String(p.id), label: p.name }))]}
                      onChange={(e) => setPlatformId(e.target.value)}
                    />
                  )}
                </FormField>
              ) : (
                <fieldset className="grid gap-2">
                  <legend className="mb-1 text-xs font-semibold text-ink">Searches</legend>
                  {status.search.kinds.map((kind) => (
                    <label key={kind} className="flex items-center gap-2 text-[13px] text-ink">
                      <input type="checkbox" checked={kinds.includes(kind)} onChange={toggleKind(kind)} className="h-4 w-4 accent-accent-solid" />
                      {SEARCH_KIND_LABELS[kind]}
                    </label>
                  ))}
                  {errors.kinds ? <p className="text-xs text-bad">{errors.kinds}</p> : null}
                </fieldset>
              )}
              <FormField label="State" required error={errors.state ?? null}>
                {({ id, describedBy, invalid }) => (
                  <SelectInput
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    value={state}
                    options={[{ value: '', label: 'Pick a state' }, ...Object.entries(status.search.states).map(([value, label]) => ({ value, label }))]}
                    onChange={(e) => setState(e.target.value)}
                  />
                )}
              </FormField>
            </>
          ) : null
        ) : (
          <FormField label={adapter === 'manual' ? 'URLs' : 'Slugs or links'} required hint={`One per line, at most ${status.limits.max_urls}.`} error={errors.urls ?? null}>
            {({ id, describedBy, invalid }) => (
              <Textarea id={id} aria-describedby={describedBy} invalid={invalid} rows={6} value={urls} onChange={(e) => setUrls(e.target.value)} placeholder={placeholder} disabled={pending} />
            )}
          </FormField>
        )}
      </div>
    </Dialog>
  )
}

/** The Lead finder's frame: title, what it's for, today's budget, Start a run and the two tabs. */
export function FinderFrame({ children }: { children: ReactNode }) {
  const { data: status, refetch } = useFinderStatus()
  const [starting, setStarting] = useState(false)
  // So the button comes back, and the budget moves, while a run goes on elsewhere.
  usePoll(!!status?.active_run_id, refetch)
  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Find leads"
        description={
          status
            ? `Search the web for public leagues, then add the ones you want to Leads. ${formatCount(status.budget.used)} of ${formatCount(status.budget.limit)} pages used today.`
            : 'Search the web for public leagues, then add the ones you want to Leads.'
        }
        actions={
          <Button variant="primary" icon={<PlusIcon size={16} />} disabled={!status || status.active_run_id !== null} onClick={() => setStarting(true)}>
            Start a run
          </Button>
        }
      />
      {status?.active_run_id ? <InlineAlert>A run is going. A second one waits until it finishes.</InlineAlert> : null}
      <Tabs
        label="Lead finder sections"
        items={[
          { to: '/growth/finder/queue', label: 'Review queue' },
          { to: '/growth/finder/runs', label: 'Runs' },
        ]}
      />
      {children}
      {status ? <RunDialog open={starting} onClose={() => setStarting(false)} status={status} /> : null}
    </div>
  )
}
